import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { applyTheme } from "../../lib/colors";

interface SiteSettings {
  primaryColor: string;
  secondaryColor: string;
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function ColorField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-sm font-semibold text-ink-700">{label}</p>
      <p className="mt-0.5 text-xs text-ink-500 mb-3">{description}</p>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-16 cursor-pointer rounded border border-ink-300 p-0.5"
        />
        <input
          type="text"
          value={value}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          className="input w-32 font-mono uppercase"
          placeholder="#000000"
        />
        <span
          className="h-9 w-9 rounded-full border border-ink-300"
          style={{ backgroundColor: HEX_RE.test(value) ? value : undefined }}
        />
      </div>
    </div>
  );
}

export function AdminSettings() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["site-settings"],
    queryFn: () => api<SiteSettings>("/settings"),
  });

  const [primary, setPrimary] = useState("#DC2626");
  const [secondary, setSecondary] = useState("#0891B2");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setPrimary(data.primaryColor);
      setSecondary(data.secondaryColor);
    }
  }, [data]);

  // Aperçu en temps réel
  useEffect(() => {
    if (HEX_RE.test(primary) && HEX_RE.test(secondary)) {
      applyTheme(primary, secondary);
    }
  }, [primary, secondary]);

  const save = useMutation({
    mutationFn: () =>
      api("/admin/settings", {
        method: "PUT",
        json: { primaryColor: primary, secondaryColor: secondary },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const canSave = HEX_RE.test(primary) && HEX_RE.test(secondary) && !save.isPending;

  return (
    <div className="space-y-8 max-w-2xl">
      <h1 className="h-section">Apparence</h1>

      <div className="card space-y-7">
        <ColorField
          label="Couleur principale"
          description="Boutons CTA, éléments mis en avant, liens actifs du menu"
          value={primary}
          onChange={setPrimary}
        />

        <ColorField
          label="Couleur secondaire"
          description="Boutons outline, badges, liens secondaires"
          value={secondary}
          onChange={setSecondary}
        />

        {/* Aperçu */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-ink-500 mb-3">
            Aperçu en temps réel
          </p>
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-ink-100 p-4">
            <button className="btn-primary">Réserver</button>
            <button className="btn-outline">En savoir plus</button>
            <span className="eyebrow">Nouveauté</span>
            <span className="inline-flex items-center rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-700">
              Principal
            </span>
            <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
              Secondaire
            </span>
          </div>
        </div>

        {/* Palette générée */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-ink-500 mb-3">
            Palette générée
          </p>
          <div className="space-y-2">
            {(["brand", "accent"] as const).map((scale) => (
              <div key={scale} className="flex gap-1">
                {[50, 100, 200, 300, 400, 500, 600, 700, 900].map((shade) => (
                  <div key={shade} className="flex flex-col items-center gap-1">
                    <div
                      style={{ backgroundColor: `rgb(var(--${scale}-${shade}))` }}
                      className="h-8 w-8 rounded border border-black/10"
                      title={`${scale}-${shade}`}
                    />
                    {scale === "brand" && (
                      <span className="text-[10px] text-ink-500">{shade}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={!canSave}>
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </button>
        {saved && (
          <span className="text-sm font-medium text-green-600">✓ Couleurs sauvegardées</span>
        )}
        {save.isError && (
          <span className="text-sm text-brand-500">Erreur lors de la sauvegarde</span>
        )}
      </div>
    </div>
  );
}
